// PlayersHub — Phase 3.5 consolidated Players page.
//
// Replaces the old PeopleHub + FinanceHub split for the player-side
// workflows. The bottom-nav "Players" tab lands here directly; the
// page owns its own header + the two sub-tabs from the wireframe:
//
//   /people/players                → redirect → /people/players/requests
//   /people/players/requests       → PlayerRequestsPanel (QR + queues)
//   /people/players/list           → PlayersListPanel    (user roster)
//
// Detail/workflow routes (player detail, onboard) sit at sibling paths
// and are full pages without these sub-tabs:
//   /people/players/:id
//   /people/players/new
// (Manual recharge no longer has its own page — the canonical entry
//  point is /people/players/requests?preselect=<id>.)
//
// Wallet sync: refreshAgentWallet() fires on mount so navigating to
// the Players page always pulls a fresh pool balance (matches the
// "auto-refetch on Players/Agents page" requirement). The panel
// component does its own refresh too — belt + braces.

import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import { useT } from '@baaligames/shared-ui/lib/i18n';

// Phase R7 follow-up (2026-05-13): how often the hub re-polls the
// pending-counts so the magenta badge on the "Player requests" tab
// stays fresh while the agent is on the page. 30 s was the original
// trade-off; 2026-05-20 bumped to 60 s to match the new bottom-nav
// PENDING_POLL_MS rhythm — fresh submissions still surface within a
// minute (acceptable UX), and we stop doubling-up with the bottom-nav.
const COUNT_POLL_MS = 60_000;

export default function PlayersHub() {
  const t = useT();
  const loc = useLocation();

  // Auto-refetch the wallet on every navigation into the Players page
  // so the chrome chip is never stale before the agent acts on a
  // request or fires a manual recharge.
  useEffect(() => {
    refreshAgentWallet();
  }, [loc.pathname]);

  // Phase R7 follow-up (2026-05-13): poll the pending-request totals
  // so the "Player requests" tab badge is always live. Sums the
  // recharge + withdrawal pending counts — clicking through into the
  // requests panel exposes the per-direction breakdown.
  const [pending, setPending] = useState<{ recharge: number; withdrawal: number }>({ recharge: 0, withdrawal: 0 });
  useEffect(() => {
    let alive = true;
    const fetchCounts = async () => {
      try {
        const [r, w] = await Promise.all([
          adminApi.get<{ total: number }>('/admin/payment-requests/recharge?state=pending&limit=1&offset=0'),
          adminApi.get<{ total: number }>('/admin/payment-requests/withdrawal?state=pending&limit=1&offset=0'),
        ]);
        if (alive) setPending({ recharge: r.total || 0, withdrawal: w.total || 0 });
      } catch { /* badge stays at last good value */ }
    };
    fetchCounts();
    // 2026-05-20 — skip when tab is hidden.
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchCounts();
    }, COUNT_POLL_MS);
    // Also refresh whenever the user comes back from a sub-route
    // (e.g. they approved a request and navigated back). Watching
    // loc.pathname covers most cases without an extra event hook.
    return () => { alive = false; clearInterval(iv); };
  }, [loc.pathname]);
  const totalPending = pending.recharge + pending.withdrawal;

  return (
    <div className="max-w-xl lg:max-w-3xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="px-2 lg:px-4"
      >
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-sky-50 text-sky-700 ring-1 ring-sky-100
                           dark:bg-sky-400/15 dark:text-sky-300 dark:ring-sky-400/30">
            <Icon name="group" size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-display font-semibold text-arena-900 dark:text-white tracking-tight">
              {t('agent.people.hub.players.title')}
            </h1>
            <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 truncate">
              {t('agent.people.hub.players.subtitle')}
            </p>
          </div>
        </div>
      </motion.header>

      {/* Top sub-tabs — magenta rewards-style pills. */}
      <div className="px-2 lg:px-4">
        <MagentaTabBar
          size="lg"
          accent="sky"
          items={[
            { to: '/people/players/requests', label: t('agent.people.hub.players.subtab.requests'), icon: 'inbox', matchPrefix: '/people/players/requests', count: totalPending },
            { to: '/people/players/list',     label: t('agent.people.hub.players.subtab.list'),     icon: 'group', matchPrefix: '/people/players/list' },
          ]}
        />
      </div>

      {/* Active panel */}
      <div className="px-2 lg:px-4">
        <Outlet />
      </div>
    </div>
  );
}
